import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, CalendarPlus, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLang } from "@/context/LanguageContext";
import { api, API, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { useSyncedReload } from "@/hooks/useSyncedReload";
import { Field, inputCls, withRef, RefBadge } from "@/components/shared";

const emptyViewing = {
  buyer_id: "", buyer_name: "", property_id: "", property_name: "", date_time: "",
  general_reaction: "", liked: "", concerns: "", other_properties: "", decision_date: "", next_action: "",
};

/** Viewings tab inside the property dialog — lists and manages this property's viewings. */
export default function PropertyViewings({ propertyId, propertyName }) {
  const { t, lang } = useLang();
  const [viewings, setViewings] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const [v, b] = await Promise.all([
        api.get("/viewings", { params: { property_id: propertyId } }),
        api.get("/buyers"),
      ]);
      setViewings(v.data);
      setBuyers(b.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);
  useSyncedReload(load);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    if (!form.buyer_name.trim()) return toast.error(t("required"));
    setSaving(true);
    try {
      if (form.id) await api.put(`/viewings/${form.id}`, form);
      else await api.post("/viewings", form);
      toast.success(t("saved"));
      setForm(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/viewings/${id}`);
      toast.success(t("deleted"));
      setForm(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="property-viewings-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg text-stone-900">{t("viewings")}</h3>
          <p className="text-xs text-stone-500 mt-0.5">{t("propertyViewingsSubtitle")}</p>
        </div>
        {!form && (
          <Button
            data-testid="property-add-viewing-button"
            onClick={() => setForm({ ...emptyViewing, property_id: propertyId, property_name: propertyName || "" })}
            className="bg-stone-900 hover:bg-stone-800 text-white h-8 text-xs"
          >
            <Plus size={14} className="mr-1" /> {t("newViewing")}
          </Button>
        )}
      </div>

      {form && (
        <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 space-y-3" data-testid="property-viewing-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t("buyer")}>
              <Select value={form.buyer_id || "custom"}
                onValueChange={(v) => {
                  const b = buyers.find((x) => x.id === v);
                  setForm({ ...form, buyer_id: v === "custom" ? "" : v, buyer_name: b ? b.name : form.buyer_name });
                }}>
                <SelectTrigger data-testid="property-viewing-buyer-select" className={inputCls}>
                  <SelectValue placeholder={t("buyer")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">—</SelectItem>
                  {buyers.map((b) => <SelectItem key={b.id} value={b.id}>{withRef(b.ref_code, b.name)}</SelectItem>)}
                </SelectContent>
              </Select>
              <input data-testid="property-viewing-buyer-name-input" className={`${inputCls} mt-2`} placeholder={t("name")}
                value={form.buyer_name} onChange={set("buyer_name")} />
            </Field>
            <div className="space-y-3">
              <Field label={t("dateTime")}>
                <input data-testid="property-viewing-datetime-input" type="datetime-local" className={inputCls} value={form.date_time} onChange={set("date_time")} />
              </Field>
              <Field label={t("decisionDate")}>
                <input data-testid="property-viewing-decision-input" type="date" className={inputCls} value={form.decision_date} onChange={set("decision_date")} />
              </Field>
            </div>
            <Field label={t("generalReaction")} className="sm:col-span-2">
              <textarea data-testid="property-viewing-reaction-input" rows={2} className={inputCls} value={form.general_reaction} onChange={set("general_reaction")} />
            </Field>
            <Field label={t("liked")}><textarea rows={2} className={inputCls} value={form.liked} onChange={set("liked")} /></Field>
            <Field label={t("concerns")}><textarea rows={2} className={inputCls} value={form.concerns} onChange={set("concerns")} /></Field>
            <Field label={t("nextAction")} className="sm:col-span-2">
              <input data-testid="property-viewing-nextaction-input" className={inputCls} value={form.next_action} onChange={set("next_action")} />
              <p className="text-[11px] text-stone-400 mt-1">{t("autoTaskHint")}</p>
            </Field>
          </div>
          <div className="flex items-center justify-between">
            {form.id ? (
              <button data-testid="property-viewing-delete-button" onClick={() => remove(form.id)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-[#7F1D1D] hover:underline">
                <Trash2 size={14} strokeWidth={1.5} /> {t("delete")}
              </button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="ghost" data-testid="property-viewing-cancel-button" onClick={() => setForm(null)}>{t("cancel")}</Button>
              <Button data-testid="property-viewing-save-button" onClick={save} disabled={saving} className="bg-stone-900 hover:bg-stone-800 text-white">
                {t("save")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {loading && <div className="text-sm text-stone-400 text-center py-4">{t("loading")}...</div>}

      {!loading && viewings.length === 0 && !form && (
        <div className="text-center py-8 text-stone-400 text-sm italic border border-dashed border-stone-200 rounded-xl">
          {t("noViewings")}
        </div>
      )}

      <div className="space-y-2">
        {viewings.map((v) => {
          const buyerRef = (buyers.find((b) => b.id === v.buyer_id) || {}).ref_code;
          return (
            <div key={v.id} data-testid={`property-viewing-row-${v.id}`}
              className="bg-white border border-stone-200 rounded-xl p-3 hover:border-stone-300 transition-colors">
              <div className="flex flex-wrap items-start gap-3">
                <button className="flex-1 min-w-0 text-left" data-testid={`property-viewing-edit-${v.id}`}
                  onClick={() => setForm({ ...emptyViewing, ...v })}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Eye size={13} strokeWidth={1.5} className="text-[#9F8E7E]" />
                    <span className="text-sm font-medium text-stone-900">{v.buyer_name || "—"}</span>
                    {buyerRef && <RefBadge code={buyerRef} />}
                  </div>
                  <p className="text-xs text-stone-400 mt-1">
                    {v.date_time ? `${fmtDate(v.date_time, lang)} ${v.date_time.slice(11, 16)}` : "—"}
                    {v.decision_date ? ` · ${t("decisionBy")} ${fmtDate(v.decision_date, lang)}` : ""}
                  </p>
                  {v.general_reaction && <p className="text-xs text-stone-600 mt-1 line-clamp-1">{v.general_reaction}</p>}
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  {v.date_time && (
                    <a data-testid={`property-viewing-ics-${v.id}`} href={`${API}/viewings/${v.id}/ics`} title={t("addToCalendar")}
                      className="p-1.5 rounded-lg text-stone-400 hover:text-stone-700 hover:bg-stone-50 border border-stone-200">
                      <CalendarPlus size={14} strokeWidth={1.5} />
                    </a>
                  )}
                  <button data-testid={`property-viewing-remove-${v.id}`} title={t("delete")} onClick={() => remove(v.id)}
                    className="p-1.5 rounded-lg text-stone-400 hover:text-red-600 hover:bg-red-50">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
